import {useEffect, useRef, useState} from 'react';

import i18next from 'i18next';

import {fetchDocumentGenerateId} from 'api/documents/create';
import {RestError, showRestError} from 'core/errors';

export function useRecallDocument(identity: string) {
    const request = useRef<{key: string; id: string} | null>(null);
    const running = useRef(false);
    const mounted = useRef(true);
    const revision = useRef(0);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        request.current = null;
        revision.current += 1;
    }, [identity]);

    useEffect(
        () => () => {
            mounted.current = false;
            revision.current += 1;
        },
        [],
    );

    const submit = async <T extends object>(
        payload: T,
        send: (value: T & {documentId: string}) => Promise<unknown>,
    ): Promise<boolean> => {
        if (running.current) return false;
        running.current = true;
        setPending(true);
        const currentRevision = revision.current;
        const key = JSON.stringify(payload);
        try {
            if (request.current?.key !== key) {
                request.current = null;
                try {
                    const id = await fetchDocumentGenerateId();
                    if (!id?.trim()) throw new Error(i18next.t('errors.occurred'));
                    if (!mounted.current || revision.current !== currentRevision) return false;
                    request.current = {key, id};
                } catch (error) {
                    if (mounted.current) showRestError(error as RestError);
                    return false;
                }
            }
            await send({...payload, documentId: request.current.id});
            request.current = null;
            return mounted.current && revision.current === currentRevision;
        } catch {
            // The existing effect watcher displays submission errors. Keep the ID for retry.
            return false;
        } finally {
            running.current = false;
            if (mounted.current) setPending(false);
        }
    };

    return {submit, pending};
}
